/* ============================================================================
   Mock data — garden domain (species, climate, plants, care tasks, AI scans).
   Content is deliberately realistic: long names, real districts, non-round
   numbers, plus "unhappy" records so empty/edge cases are exercised.
   ========================================================================== */

import type { Governorate, Tolerance } from '@core/types'

/** Relative date helper so the demo data never goes stale. */
export function isoFromNow(days: number, hours = 9): string {
  const date = new Date()
  date.setDate(date.getDate() + days)
  date.setHours(hours, 0, 0, 0)
  return date.toISOString()
}

export function isoMonthsAgo(months: number, day = 12): string {
  const date = new Date()
  date.setMonth(date.getMonth() - months)
  date.setDate(day)
  return date.toISOString()
}

export interface ClimateProfile {
  zone: 'arid-hot' | 'semi-arid' | 'mediterranean' | 'marsh'
  zoneLabel: string
  averageSummerHighC: number
  annualRainfallMm: number
  salinityRisk: Tolerance
  dominantSoils: string[]
}

/** Location input for recommendations — this is the data the AI service uses. */
export const CLIMATE_BY_GOVERNORATE: Record<Governorate, ClimateProfile> = {
  Baghdad: {
    zone: 'arid-hot',
    zoneLabel: 'Arid, very hot summers',
    averageSummerHighC: 44,
    annualRainfallMm: 120,
    salinityRisk: 'medium',
    dominantSoils: ['clay', 'loamy', 'saline'],
  },
  Basra: {
    zone: 'arid-hot',
    zoneLabel: 'Arid, humid heat, high salinity',
    averageSummerHighC: 46,
    annualRainfallMm: 95,
    salinityRisk: 'high',
    dominantSoils: ['saline', 'clay'],
  },
  Muthanna: {
    zone: 'arid-hot',
    zoneLabel: 'Desert fringe, extreme heat',
    averageSummerHighC: 45,
    annualRainfallMm: 85,
    salinityRisk: 'high',
    dominantSoils: ['sandy', 'saline'],
  },
  'Dhi Qar': {
    zone: 'arid-hot',
    zoneLabel: 'Arid, hot, saline soils',
    averageSummerHighC: 45,
    annualRainfallMm: 100,
    salinityRisk: 'high',
    dominantSoils: ['saline', 'clay'],
  },
  Wasit: {
    zone: 'semi-arid',
    zoneLabel: 'Semi-arid river plain',
    averageSummerHighC: 44,
    annualRainfallMm: 140,
    salinityRisk: 'medium',
    dominantSoils: ['loamy', 'clay'],
  },
  Najaf: {
    zone: 'arid-hot',
    zoneLabel: 'Arid plateau',
    averageSummerHighC: 43,
    annualRainfallMm: 110,
    salinityRisk: 'medium',
    dominantSoils: ['sandy', 'loamy'],
  },
  Karbala: {
    zone: 'arid-hot',
    zoneLabel: 'Arid plateau with irrigation',
    averageSummerHighC: 43,
    annualRainfallMm: 115,
    salinityRisk: 'medium',
    dominantSoils: ['loamy', 'chalky'],
  },
  Babil: {
    zone: 'semi-arid',
    zoneLabel: 'Irrigated river plain',
    averageSummerHighC: 44,
    annualRainfallMm: 130,
    salinityRisk: 'medium',
    dominantSoils: ['clay', 'loamy'],
  },
  Qadisiyyah: {
    zone: 'semi-arid',
    zoneLabel: 'Irrigated plain',
    averageSummerHighC: 44,
    annualRainfallMm: 135,
    salinityRisk: 'medium',
    dominantSoils: ['loamy', 'clay'],
  },
  Anbar: {
    zone: 'arid-hot',
    zoneLabel: 'Western desert',
    averageSummerHighC: 44,
    annualRainfallMm: 90,
    salinityRisk: 'medium',
    dominantSoils: ['sandy', 'chalky'],
  },
  Nineveh: {
    zone: 'semi-arid',
    zoneLabel: 'Northern plain',
    averageSummerHighC: 42,
    annualRainfallMm: 280,
    salinityRisk: 'low',
    dominantSoils: ['loamy', 'chalky'],
  },
  Saladin: {
    zone: 'semi-arid',
    zoneLabel: 'Northern river plain',
    averageSummerHighC: 42,
    annualRainfallMm: 200,
    salinityRisk: 'medium',
    dominantSoils: ['loamy', 'sandy'],
  },
  Diyala: {
    zone: 'semi-arid',
    zoneLabel: 'Foothill transition',
    averageSummerHighC: 42,
    annualRainfallMm: 250,
    salinityRisk: 'low',
    dominantSoils: ['loamy', 'clay'],
  },
  Kirkuk: {
    zone: 'semi-arid',
    zoneLabel: 'Foothill transition',
    averageSummerHighC: 41,
    annualRainfallMm: 320,
    salinityRisk: 'low',
    dominantSoils: ['loamy', 'chalky'],
  },
  Erbil: {
    zone: 'mediterranean',
    zoneLabel: 'Mediterranean foothills',
    averageSummerHighC: 39,
    annualRainfallMm: 430,
    salinityRisk: 'low',
    dominantSoils: ['loamy', 'chalky'],
  },
  Duhok: {
    zone: 'mediterranean',
    zoneLabel: 'Mediterranean, cooler nights',
    averageSummerHighC: 38,
    annualRainfallMm: 560,
    salinityRisk: 'low',
    dominantSoils: ['loamy', 'clay'],
  },
  Sulaymaniyah: {
    zone: 'mediterranean',
    zoneLabel: 'Mountain Mediterranean',
    averageSummerHighC: 37,
    annualRainfallMm: 700,
    salinityRisk: 'low',
    dominantSoils: ['loamy', 'chalky'],
  },
  Maysan: {
    zone: 'marsh',
    zoneLabel: 'Marshland, high humidity',
    averageSummerHighC: 46,
    annualRainfallMm: 130,
    salinityRisk: 'high',
    dominantSoils: ['saline', 'clay'],
  },
}
